import React, { useState, useEffect, useCallback } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import moment from 'moment-timezone'; 

// =========================================================
// Permission List
// =========================================================
const PERMISSIONS = [
    'permissonMobileBank', 'permissonAPIkey', 'permissonSendPayment', 
    'permissonAPPreceive', 'permissonGetsms', 'permissonAddsms', 
    'permissonCode', 'permissonPaymentReceived', 'permissonSeenBy',
    'permissonUserdevices', 'permissonUserlist',
    'permissonUserPasswordChange', 'permissonUserCreate',
    'permissonPaymentSendEdit', // 🔥 এই নতুন পারমিশনটি অ্যাড করা হয়েছে
     'permissonUserMobileBank',      // 🔥 নতুন পারমিশন ১
    'permissonTransactionHistory',
     'permissonAccount',
     'permissonApiview',
    'permissonJobTracker',
    'apptransactionscheck'  // 🔥 নতুন পারমিশন ২
];

const getInitialPermissions = () => PERMISSIONS.reduce((acc, p) => ({ ...acc, [p]: false }), {});

const MasterKeyManager = () => {
    const [masterKeys, setMasterKeys] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [allUsers, setAllUsers] = useState([]); 
    const [userSearch, setUserSearch] = useState(''); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Modal States
    const [showKeyModal, setShowKeyModal] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const initialKeyFormState = { name: '', key: '', allowedUsers: [], ...getInitialPermissions() };
    const [keyForm, setKeyForm] = useState(initialKeyFormState);

    // Admin States
    const [showAdminModal, setShowAdminModal] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const initialAdminFormState = { username: '', password: '' };
    const [adminForm, setAdminForm] = useState(initialAdminFormState);
    
    const fetchData = useCallback(() => {
        setLoading(true);
        setError(null);
        socketService.emit('fetchMasterKeyData'); 
    }, []);
    
    const handleConfirmation = (data) => {
        setLoading(false);
        if (data.success) {
            setSuccessMessage(data.message || '✅ Operation successful!');
            setError(null);
            fetchData(); 
            setTimeout(() => setSuccessMessage(null), 3000);
        } else {
            setError(data.message || '❌ Failed: Unknown error');
            setSuccessMessage(null);
        }
    };

    useEffect(() => {
        const socket = socketService.getSocket();
        if (!socket || !socket.connected) {
            socketService.connect();
        }

        const handleConnect = () => fetchData();

        const handleMasterKeyData = (data) => {
            setLoading(false);
            if (data.error) {
                setError(data.error);
                return;
            }
            const sortedKeys = (data.masterKeys || []).sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());
            const sortedAdmins = (data.admins || []).sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());
            
            setMasterKeys(sortedKeys);
            setAdmins(sortedAdmins);
            setAllUsers(data.allUsers || []); 
        };

        socketService.on('connect', handleConnect); 
        socketService.on('masterKeyData', handleMasterKeyData);
        
        socketService.on('masterKey-add-response', (data) => {
            handleConfirmation(data);
            if (data.success) closeModal(setKeyForm, initialKeyFormState, setShowKeyModal, setEditingKey);
        });
        
        socketService.on('masterKey-edit-response', (data) => {
            handleConfirmation(data);
            if (data.success) closeModal(setKeyForm, initialKeyFormState, setShowKeyModal, setEditingKey);
        });
        
        socketService.on('masterKey-delete-response', handleConfirmation);
        socketService.on('admin-add-response', (data) => {
            handleConfirmation(data);
            if (data.success) closeModal(setAdminForm, initialAdminFormState, setShowAdminModal, setEditingAdmin);
        });
        socketService.on('admin-edit-response', (data) => {
            handleConfirmation(data);
            if (data.success) closeModal(setAdminForm, initialAdminFormState, setShowAdminModal, setEditingAdmin);
        });
        socketService.on('admin-delete-response', handleConfirmation);
        socketService.on('globalMasterKeyRefresh', fetchData); 

        if (socket && socket.connected) fetchData();

        return () => {
            socketService.off('connect', handleConnect); 
            socketService.off('masterKeyData', handleMasterKeyData);
            socketService.off('masterKey-add-response');
            socketService.off('masterKey-edit-response');
            socketService.off('masterKey-delete-response');
            socketService.off('admin-add-response');
            socketService.off('admin-edit-response');
            socketService.off('admin-delete-response');
            socketService.off('globalMasterKeyRefresh', fetchData);
        };
    }, [fetchData]);

    const closeModal = (setForm, initialFormState, setShowModal, setEditingItem) => {
        setForm(initialFormState);
        setShowModal(false);
        setEditingItem(null);
        setUserSearch(''); 
    };

    // 🔥 ইউজার সিলেকশন হ্যান্ডলার
    const handleUserSelection = (userId) => {
        setKeyForm(prev => {
            const currentUsers = prev.allowedUsers || [];
            if (currentUsers.includes(userId)) {
                return { ...prev, allowedUsers: currentUsers.filter(id => id !== userId) }; 
            } else {
                return { ...prev, allowedUsers: [...currentUsers, userId] }; 
            }
        });
    };

    // Select All হ্যান্ডলার
    const handleSelectAllUsers = () => {
        if (keyForm.allowedUsers.length === allUsers.length) {
            setKeyForm(prev => ({ ...prev, allowedUsers: [] })); 
        } else {
            setKeyForm(prev => ({ ...prev, allowedUsers: allUsers.map(u => u._id) })); 
        }
    };

    const handleKeyFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setKeyForm(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };
    
    const openKeyAddModal = () => {
        setEditingKey(null);
        setKeyForm(initialKeyFormState);
        setShowKeyModal(true);
    };

    const openKeyEditModal = (keyData) => {
        setEditingKey(keyData);
        const currentPermissions = PERMISSIONS.reduce((acc, p) => ({ ...acc, [p]: !!keyData[p] }), {});
        
        const allowedUserIds = keyData.allowedUsers ? keyData.allowedUsers.map(u => u._id || u) : [];

        setKeyForm({
            name: keyData.name,
            key: keyData.key,
            allowedUsers: allowedUserIds,
            ...currentPermissions
        });
        setShowKeyModal(true);
    };

    const handleKeySave = (e) => {
        e.preventDefault();
        if (!keyForm.name || !keyForm.key) {
            alert('Name and Key are required.');
            return;
        }
        setLoading(true);
        const dataToSend = { 
            ...keyForm, 
            ...PERMISSIONS.reduce((acc, p) => ({ ...acc, [p]: !!keyForm[p] }), {}) 
        };
        if (editingKey) {
            dataToSend.id = editingKey._id; 
            socketService.emit('masterKey-edit', dataToSend);
        } else {
            socketService.emit('masterKey-add', dataToSend);
        }
    };

    // Admin Handlers
    const handleAdminFormChange = (e) => { const { name, value } = e.target; setAdminForm(prev => ({ ...prev, [name]: value })); };
    const openAdminAddModal = () => { setEditingAdmin(null); setAdminForm(initialAdminFormState); setShowAdminModal(true); };
    const openAdminEditModal = (adminData) => { setEditingAdmin(adminData); setAdminForm({ username: adminData.username, password: '' }); setShowAdminModal(true); };
    const handleAdminSave = (e) => { e.preventDefault(); if (!adminForm.username) return alert('Username required'); if (!editingAdmin && !adminForm.password) return alert('Password required'); setLoading(true); const dataToSend = { ...adminForm }; if (editingAdmin) { dataToSend.id = editingAdmin._id; if (!dataToSend.password) delete dataToSend.password; socketService.emit('admin-edit', dataToSend); } else { socketService.emit('admin-add', dataToSend); } };
    const handleKeyDelete = (id) => { if (window.confirm('Sure?')) { setLoading(true); socketService.emit('masterKey-delete', { id }); }};
    const handleAdminDelete = (id) => { if (window.confirm('Sure?')) { setLoading(true); socketService.emit('admin-delete', { id }); }};

    const filteredUsers = allUsers.filter(u => u.username.toLowerCase().includes(userSearch.toLowerCase()));

    // =========================================================
    // 🔥 NEW: Logout All Handler
    // =========================================================
    const handleLogoutAll = () => {
        const confirmAction = window.confirm("⚠️ Are you sure? This will log out ALL admins immediately (including you)!");
        if (confirmAction) {
            const adminId = localStorage.getItem('adminId');
            // সার্ভারে ইভেন্ট পাঠানো
            socketService.emit('admin-logout-all', { 
                requesterId: adminId 
            });
        }
    };

    return (
        <Layout>
            <div className="main-content-area py-3 px-10">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-title m-0">🔑 Master Key & Admin Management</h5>
                </div>
                
                {successMessage && <div className="alert bg-seen border-0 mb-3 small fw-bold">✅ {successMessage}</div>}
                {error && <div className="alert bg-fake border-0 mb-3 small fw-bold text-white">❌ {error}</div>}
                
                {/* 1. Master Key Table */}
                <div className="table-container shadow-sm border-0 mb-4">
                    <div className="px-4 py-3 bg-white border-bottom d-flex justify-content-between align-items-center">
                        <h6 className="m-0 fw-bold text-primary">Master Keys ({masterKeys.length})</h6>
                        <button onClick={openKeyAddModal} className="btn btn-sm btn-apply fw-bold">➕ Add Master Key</button>
                    </div>
                    <div className="table-responsive">
                        <table className="custom-zebra-table">
                            <thead>
                                <tr>
                                    <th className="ps-4">NAME</th>
                                    <th>KEY</th>
                                    <th style={{minWidth: '200px'}}>ALLOWED USERS</th>
                                    <th style={{width: '30%'}}>PERMISSIONS</th>
                                    <th className="text-end pe-4">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {masterKeys.map((key) => (
                                    <tr key={key._id}>
                                        <td className="ps-4"><div className="user-primary">{key.name}</div></td>
                                        <td><code className="sim-num">{key.key}</code></td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {key.allowedUsers && key.allowedUsers.length > 0 ? (
                                                    key.allowedUsers.map((u, i) => (
                                                        <span key={i} className="badge bg-info text-white border" style={{fontSize: '9px'}}>
                                                            {u.username || 'Unknown'}
                                                        </span>
                                                    ))
                                                ) : <span className="text-muted small">All / No Restriction</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {PERMISSIONS.filter(p => key[p]).map(p => (
                                                    <span key={p} className="badge bg-light text-dark border" style={{fontSize: '9px'}}>
                                                        {p.replace('permisson', '')}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="text-end pe-4">
                                            <button className="btn btn-sm btn-light border me-1" onClick={() => openKeyEditModal(key)}>✏️</button>
                                            <button className="btn btn-sm btn-light border text-danger" onClick={() => handleKeyDelete(key._id)}>🗑</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 2. Admin Table */}
                <div className="table-container shadow-sm border-0 mb-4">
                    <div className="px-4 py-3 bg-white border-bottom d-flex justify-content-between align-items-center">
                        <h6 className="m-0 fw-bold text-primary">Admin Accounts ({admins.length})</h6>
                        <button onClick={openAdminAddModal} className="btn btn-sm btn-apply fw-bold">➕ Add Admin</button>
                    </div>
                    <div className="table-responsive">
                        <table className="custom-zebra-table">
                            <thead><tr><th className="ps-4">USERNAME</th><th>CREATED</th><th className="text-end pe-4">ACTIONS</th></tr></thead>
                            <tbody>
                                {admins.map((admin) => (
                                    <tr key={admin._id}>
                                        <td className="ps-4"><div className="user-primary">{admin.username}</div></td>
                                        <td><div className="time-muted">{moment(admin.createdAt).format('YYYY-MM-DD HH:mm')}</div></td>
                                        <td className="text-end pe-4">
                                            <button className="btn btn-sm btn-light border me-1" onClick={() => openAdminEditModal(admin)}>✏️</button>
                                            <button className="btn btn-sm btn-light border text-danger" onClick={() => handleAdminDelete(admin._id)}>🗑</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 3. DANGER ZONE - Logout All */}
                <div className="shadow-sm border border-danger rounded bg-white p-4 mb-4" style={{ backgroundColor: '#fff5f5', borderColor: '#fc8181' }}>
                    <h6 className="fw-bold text-danger">🚨 Danger Zone</h6>
                    <p className="small text-muted mb-3">
                        Performing this action will immediately log out <strong>ALL</strong> currently logged-in administrators. 
                        Everyone will need to log in again.
                    </p>
                    <button 
                        onClick={handleLogoutAll} 
                        className="btn btn-danger btn-sm fw-bold px-4"
                    >
                        ⚠️ Force Logout All Admins
                    </button>
                </div>
            </div>

            {/* Master Key Modal */}
            {showKeyModal && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal-dialog modal-lg">
                        <div className="custom-modal-content">
                            <div className="modal-header bg-light">
                                <h6 className="m-0 fw-bold">{editingKey ? '✏️ Edit Master Key' : '➕ Add Master Key'}</h6>
                                <button type="button" className="btn-close" onClick={() => closeModal(setKeyForm, initialKeyFormState, setShowKeyModal, setEditingKey)}></button>
                            </div>
                            <form onSubmit={handleKeySave}>
                                <div className="modal-body p-4">
                                    <div className="row g-3 mb-4">
                                        <div className="col-md-6">
                                            <label className="field-label">Name (Admin User)</label>
                                            <select name="name" value={keyForm.name} onChange={handleKeyFormChange} className="modern-input" required>
                                                <option value="">-- Choose Admin --</option>
                                                {admins.map(admin => (<option key={admin._id} value={admin.username}>{admin.username}</option>))}
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="field-label">Key</label>
                                            <input type="text" name="key" value={keyForm.key} onChange={handleKeyFormChange} className="modern-input" required placeholder="Enter key..." />
                                        </div>
                                    </div>
                                    
                                    <div className="row">
                                        {/* 🔥 User Selection List with Search & Select All */}
                                        <div className="col-md-6 border-end">
                                            <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                                                <h6 className="field-label m-0">Allowed Users</h6>
                                                <button type="button" className="btn btn-xs btn-light border py-0 px-2" style={{fontSize:'10px'}} onClick={handleSelectAllUsers}>
                                                    {keyForm.allowedUsers.length === allUsers.length ? 'Deselect All' : 'Select All'}
                                                </button>
                                            </div>
                                            
                                            <input 
                                                type="text" 
                                                className="modern-input mb-2 py-1" 
                                                placeholder="🔍 Search user..." 
                                                style={{fontSize:'12px'}}
                                                value={userSearch}
                                                onChange={(e) => setUserSearch(e.target.value)}
                                            />

                                            <div style={{maxHeight: '250px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px'}}>
                                                {filteredUsers.map(user => {
                                                    const isSelected = keyForm.allowedUsers.includes(user._id);
                                                    return (
                                                        <div 
                                                            key={user._id} 
                                                            className={`form-check p-2 border-bottom m-0 hover-bg ${isSelected ? 'selected-user-row' : ''}`}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => handleUserSelection(user._id)}
                                                        >
                                                            <input 
                                                                type="checkbox" 
                                                                className="form-check-input cursor-pointer"
                                                                checked={isSelected}
                                                                onChange={() => {}} 
                                                                id={`user-${user._id}`}
                                                                style={{ pointerEvents: 'none' }}
                                                            />
                                                            <label className="form-check-label ms-2 cursor-pointer w-100 fw-bold text-dark" htmlFor={`user-${user._id}`} style={{fontSize:'13px', pointerEvents: 'none'}}>
                                                                {user.username}
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                                {filteredUsers.length === 0 && <p className="text-muted small text-center py-2">No users match.</p>}
                                            </div>
                                            <div className="text-end mt-1">
                                                <small className="text-muted" style={{fontSize:'10px'}}>{keyForm.allowedUsers.length} users selected</small>
                                            </div>
                                        </div>

                                        {/* Permissions */}
                                        <div className="col-md-6">
                                            <h6 className="field-label border-bottom pb-2 mb-2">Permissions</h6>
                                            <div className="row g-2">
                                                {PERMISSIONS.map(p => (
                                                    <div className="col-12" key={p}>
                                                        <div className="form-check bg-light p-2 rounded border">
                                                            <input type="checkbox" name={p} id={`key-${p}`} checked={keyForm[p] || false} onChange={handleKeyFormChange} className="form-check-input ms-0 me-2" />
                                                            <label className="form-check-label small" htmlFor={`key-${p}`} style={{fontSize: '11px', fontWeight: '600'}}>
                                                                {p.replace('permisson', '')}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-reset flex-fill py-2" onClick={() => closeModal(setKeyForm, initialKeyFormState, setShowKeyModal, setEditingKey)}>Cancel</button>
                                    <button type="submit" className="btn btn-apply flex-fill py-2" disabled={loading}>Save Changes</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Admin Modal */}
            {showAdminModal && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal-dialog">
                        <div className="custom-modal-content">
                            <div className="modal-header bg-light">
                                <h6 className="m-0 fw-bold">{editingAdmin ? '✏️ Edit Admin' : '➕ Add Admin'}</h6>
                                <button type="button" className="btn-close" onClick={() => closeModal(setAdminForm, initialAdminFormState, setShowAdminModal, setEditingAdmin)}></button>
                            </div>
                            <form onSubmit={handleAdminSave}>
                                <div className="modal-body p-4">
                                    <div className="mb-3">
                                        <label className="field-label">Username</label>
                                        <input type="text" name="username" value={adminForm.username} onChange={handleAdminFormChange} className="modern-input" required disabled={!!editingAdmin} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="field-label">{editingAdmin ? 'New Password' : 'Password'}</label>
                                        <input type="password" name="password" value={adminForm.password} onChange={handleAdminFormChange} className="modern-input" required={!editingAdmin} />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-reset flex-fill py-2" onClick={() => closeModal(setAdminForm, initialAdminFormState, setShowAdminModal, setEditingAdmin)}>Cancel</button>
                                    <button type="submit" className="btn btn-apply flex-fill py-2">Save</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .field-label { font-size: 11px; font-weight: 700; color: #64748b; margin-bottom: 5px; text-transform: uppercase; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; transition: 0.2s; }
                .modern-input:focus { border-color: #0284c7; background: #fff; }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 13px; border: none; transition: 0.3s; }
                .btn-apply:hover { background: #0369a1; }
                .btn-reset { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #e2e8f0; color: #475569; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #cbd5e1; text-align: left; }
                .custom-zebra-table tbody tr:nth-child(even) { background-color: #f8fafc; }
                .custom-zebra-table tbody tr:hover { background-color: #edf2f7 !important; }
                .custom-zebra-table td { padding: 12px 15px; vertical-align: middle; border-bottom: 1px solid #edf2f7; }
                .user-primary { color: #0369a1; font-weight: 700; font-size: 16px; }
                .sim-num { color: #1e293b; font-weight: 700; font-size: 13px; }
                .time-muted { font-size: 11px; color: #64748b; font-weight: 600; }
                .bg-seen { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
                .bg-fake { background: #fee2e2; color: #dc2626; border-color: #fecaca; }
                .custom-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.7); z-index: 99999 !important; display: flex; justify-content: center; align-items: flex-start; padding-top: 60px; overflow-y: auto; }
                .custom-modal-dialog { background: #fff; width: 90%; max-width: 550px; margin: 0 auto 50px auto; border-radius: 12px; box-shadow: 0 20px 40px rgba(0,0,0,0.4); position: relative; animation: slideUp 0.3s ease-out; display: flex; flex-direction: column; }
                .custom-modal-dialog.modal-lg { max-width: 900px; }
                .custom-modal-content { border: none; overflow: hidden; border-radius: 12px; width: 100%; }
                .modal-header { padding: 15px 20px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; background: #ffffff; border-radius: 12px 12px 0 0; }
                .modal-body { padding: 20px; overflow-x: hidden; }
                .modal-footer { padding: 15px 20px; border-top: none; display: flex; gap: 10px; background: #fff; border-radius: 0 0 12px 12px; }
                .btn-close { font-size: 12px; background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat; width: 1em; height: 1em; padding: 0.5em; opacity: 0.5; border: 0; cursor: pointer; }
                .btn-close:hover { opacity: 0.8; }
                .hover-bg:hover { background-color: #f8fafc; }
                .cursor-pointer { cursor: pointer; }
                
                /* 🔥 সিলেক্টেড ইউজারের জন্য হাইলাইট কালার */
                .selected-user-row {
                    background-color: #e0f2fe !important; /* হালকা নীল */
                    border-left: 3px solid #0284c7; /* বামে নীল বর্ডার */
                }

                @keyframes slideUp { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            `}</style>
        </Layout>
    );
};

export default MasterKeyManager;